// Quotations module header card + navigation (default export `PageButtons`, imported as `Tabs`).
// Wraps all three quotation pages: CrmQuotationList (/quotation-list), CrmQuotationCretion (/quotation-creation)
// and CrmViewQuotation (/quotations). It also pre-loads the Redux data those pages read.
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "@mui/material/Button";
import ListIcon from '@mui/icons-material/List';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Avatar, Box, Stack, Typography } from "@mui/material";
import { APP_ROUTES } from "../../../config/appRoutes";
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { useDispatch } from "react-redux";
import { getQuotationTemplateThunk, getQuotationLogThunk } from "../quotationThunk";
import { getContactThunk } from "../../contact/contactThunk";
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { QR } from "../../qrCode/components/qrUi";

// Header card for the quotation pages (same look as Templates / QR).
// Optional: `stats` (cards inside the header) and `children` (page content inside the card).
export default function PageButtons({ stats = [], children }) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    // Path of the highlighted nav pill; kept in sync with the URL by the effect below.
    const [activePath, setActivePath] = useState(location.pathname);

    useEffect(() => {
        setActivePath(location.pathname);
    }, [location.pathname]);

    // Navigates to one of the quotation pages and marks its pill active.
    const handleClick = (path) => {
        setActivePath(path);
        navigate(path);
    };

    // On mount: load templates, sent-quotation logs and contacts (contacts feed the "Select User" dropdown
    // on the create page). The two quotation thunks return cached store data when it is already loaded.
    useEffect(() => {
        dispatch(getQuotationTemplateThunk());
        dispatch(getQuotationLogThunk());
        dispatch(getContactThunk());
    }, [dispatch]);

    // Three destinations → compact segmented pill group (all stay reachable)
    const navItems = [
        { path: APP_ROUTES.QUOTATION.LIST, label: "Quotation List", icon: <ListIcon sx={{ fontSize: 18 }} /> },
        { path: APP_ROUTES.QUOTATION.CREATION, label: "Create Quotation", icon: <AddCircleOutlineIcon sx={{ fontSize: 18 }} /> },
        { path: APP_ROUTES.QUOTATION.QUOTATION_LIST, label: "View Quotations", icon: <Inventory2OutlinedIcon sx={{ fontSize: 18 }} /> },
    ];

    return (
        <Box
            sx={{
                position: "relative",
                overflow: "hidden",
                p: { xs: 1.5, sm: 2, md: 2.5 },
                borderRadius: 3,
                border: `1px solid ${QR.border}`,
                bgcolor: "#fff",
                boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
            }}
        >
            {/* Top row: title + navigation pills */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar
                        variant="rounded"
                        sx={{
                            width: { xs: 40, sm: 46 },
                            height: { xs: 40, sm: 46 },
                            borderRadius: 2.5,
                            color: "#fff",
                            background: `linear-gradient(135deg, ${QR.main}, ${QR.light})`,
                            boxShadow: `0 8px 18px -8px ${QR.main}`,
                        }}
                    >
                        <RequestQuoteIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight={800} color="#0f172a" lineHeight={1.2} sx={{ fontSize: { xs: 17, sm: 20 } }}>
                            Quotations
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                            Build quotations, preview the PDF and send them on WhatsApp
                        </Typography>
                    </Box>
                </Stack>

                <Box
                    sx={{
                        display: "flex",
                        gap: 0.5,
                        p: 0.5,
                        borderRadius: 3,
                        bgcolor: "#f1f5f9",
                        border: `1px solid ${QR.border}`,
                        width: { xs: "100%", md: "auto" },
                        flexDirection: { xs: "column", sm: "row" },
                    }}
                >
                    {/* One pill per page; the active one gets the blue gradient. */}
                    {navItems.map((item) => {
                        const active = activePath === item.path;
                        return (
                            <Button
                                key={item.path}
                                onClick={() => handleClick(item.path)}
                                startIcon={item.icon}
                                sx={{
                                    flex: { xs: "1 1 auto", sm: 1, md: "0 0 auto" },
                                    height: 36,
                                    px: 1.75,
                                    borderRadius: 2.5,
                                    textTransform: "none",
                                    fontSize: 13.5,
                                    fontWeight: 700,
                                    whiteSpace: "nowrap",
                                    color: active ? "#fff" : "#475569",
                                    background: active ? `linear-gradient(135deg, ${QR.blue}, #3b82f6)` : "transparent",
                                    boxShadow: active ? `0 6px 14px -8px ${QR.blue}` : "none",
                                    "&:hover": {
                                        background: active ? `linear-gradient(135deg, ${QR.blue}, #3b82f6)` : "#fff",
                                        color: active ? "#fff" : QR.blue,
                                    },
                                }}
                            >
                                {item.label}
                            </Button>
                        );
                    })}
                </Box>
            </Box>

            {/* Stats cards inside the header (only when the page passes stats) */}
            {stats.length > 0 && (
                <Box
                    sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: `1px solid ${QR.border}`,
                        display: "grid",
                        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: `repeat(${stats.length}, minmax(0, 1fr))` },
                        gap: 1.5,
                    }}
                >
                    {stats.map((s, idx) => (
                        <Stack
                            key={s.label}
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                                gridColumn: { xs: stats.length % 2 === 1 && idx === stats.length - 1 ? "span 2" : "auto", md: "auto" },
                                p: { xs: 1.25, sm: 1.5 },
                                borderRadius: 3,
                                bgcolor: "#fff",
                                border: `1px solid ${QR.border}`,
                                borderTop: `3px solid ${s.color}`,
                                boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
                                minWidth: 0, // lets the noWrap label ellipsis inside the 2-column phone grid
                            }}
                        >
                            <Avatar variant="rounded" sx={{ width: 40, height: 40, borderRadius: 2, color: s.color, bgcolor: `${s.color}14` }}>
                                {s.icon}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap display="block">
                                    {s.label}
                                </Typography>
                                <Typography variant="h6" fontWeight={800} lineHeight={1.2} color="#0f172a">
                                    {s.value}
                                </Typography>
                            </Box>
                        </Stack>
                    ))}
                </Box>
            )}

            {/* Page content inside the same card */}
            {children && (
                <Box sx={{ mt: 2.5, pt: 0.5, borderTop: `1px solid ${QR.border}` }}>
                    {children}
                </Box>
            )}
        </Box>
    );
}
